// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — File Library Store (Zustand)
// Manages documents and categories for the File Library module
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { fetchTable, createRecord, updateRecord, deleteRecord, TABLES, FIELDS } from '../lib/api-switch';
import { fetchRecords } from '../api/client';
import { uploadFileToV2, getV2FileDownloadUrl } from '../lib/uploadFile';

// ── Types ───────────────────────────────────────────────────────

export interface FLDocument {
  id: number;
  Rid: number;
  title: string;
  description: string;
  category: string;
  tags: string;
  fileUrl: string;
  fileType: string;
  publishDate: string;
  expirationDate: string;
  status: string;
  uploadedBy: string;
  version: string;
  priority: string;
  fileUploadUrl: string; // V2: stores documentId UUID for native attachments
  fileName: string;      // original uploaded file name
}

export interface FLCategory {
  id: number;
  Rid: number;
  name: string;
  icon: string;
  color: string;
  sortOrder: string;
  active: string;
}

// ── Mappers ─────────────────────────────────────────────────────

const FD = FIELDS.flDocuments;
const FC = FIELDS.flCategories;

interface V2Attachment {
  id: string;
  documents: Array<{ id: string; fileName: string; contentType: string; actualSize?: number }>;
}

function mapDocument(row: Record<string, unknown>): FLDocument {
  const id = Number(row.record_id ?? row.id) || 0;

  // V2 native attachments come in the _attachments object
  const attachments = row._attachments as Record<string, V2Attachment> | undefined;
  const fileAttachment = attachments?.[FD.file as string];
  const firstDoc = fileAttachment?.documents?.[0];
  const attachmentId = row[FD.file as string] as string | undefined;

  // Extract documentId from description meta marker (stored during upload).
  // documentId is the inner UUID needed for download-url; attachmentId is the linking ref.
  const rawDesc = (row[FD.description as string] as string) ?? '';
  const docIdMatch = rawDesc.match(/\|\|\|DOCID\|\|\|([^|]+)\|\|\|/);
  const documentId = docIdMatch ? docIdMatch[1] : (firstDoc?.id ?? attachmentId ?? '');
  const cleanDesc = rawDesc.replace(/\|\|\|DOCID\|\|\|.*/, '').trim();

  // Derive file type from fileName if available
  const rawFileName = firstDoc?.fileName ?? '';
  // Also try to infer fileName from the record's title if no attachment metadata
  const fileName = rawFileName || '';
  const fileType = fileName.includes('.') ? (fileName.split('.').pop()?.toLowerCase() ?? '') : '';

  return {
    id,
    Rid: id,
    title: (row[FD.title as string] as string) ?? '',
    description: cleanDesc,
    category: String(row[FD.categoryId as string] ?? ''),
    tags: (row[FD.tags as string] as string) ?? '',
    fileUrl: (row[FD.fileUrl as string] as string) ?? '',
    fileType,
    publishDate: (row[FD.publishedAt as string] as string) ?? '',
    expirationDate: (row[FD.expirationDate as string] as string) ?? '',
    status: (row[FD.status as string] as string) ?? 'published',
    uploadedBy: (row[FD.uploadedBy as string] as string) ?? '',
    version: (row[FD.version as string] as string) ?? '',
    priority: (row[FD.priority as string] as string) ?? '',
    fileUploadUrl: documentId,  // documentId for download-url endpoint (not attachmentId)
    fileName,
  };
}

function mapCategory(row: Record<string, unknown>): FLCategory {
  const id = Number(row.record_id ?? row.id) || 0;
  return {
    id,
    Rid: id,
    name: (row[FC.name as string] as string) ?? '',
    icon: '',
    color: (row[FC.color as string] as string) ?? '',
    sortOrder: (row[FC.sortOrder as string] as string) ?? '',
    active: 'true',
  };
}

// ── Store Interface ─────────────────────────────────────────────

interface FileLibraryStore {
  documents: FLDocument[];
  categories: FLCategory[];
  loading: boolean;
  error: string | null;
  _docsLoadedAt: number;   // epoch ms — 0 = never loaded
  _catsLoadedAt: number;

  loadDocuments: (force?: boolean) => Promise<void>;
  createDocument: (data: Partial<FLDocument>) => Promise<FLDocument | null>;
  updateDocument: (id: number, data: Partial<FLDocument>) => Promise<void>;
  deleteDocument: (id: number) => Promise<void>;
  uploadFile: (id: number, file: File) => Promise<void>;
  getFileDownloadUrl: (documentId: string) => Promise<string>;

  loadCategories: (force?: boolean) => Promise<void>;
  createCategory: (data: Partial<FLCategory>) => Promise<FLCategory | null>;
  updateCategory: (id: number, data: Partial<FLCategory>) => Promise<void>;
  deleteCategory: (id: number) => Promise<void>;
}

// ── Store ───────────────────────────────────────────────────────

const CACHE_TTL = 60_000; // 60 seconds — skip refetch if data is fresh

export const useFileLibraryStore = create<FileLibraryStore>((set, get) => ({
  documents: [],
  categories: [],
  loading: false,
  error: null,
  _docsLoadedAt: 0,
  _catsLoadedAt: 0,

  // ── Documents ───────────────────────────────────────────────

  loadDocuments: async (force = false) => {
    const { _docsLoadedAt, loading } = get();
    if (!force && loading) return; // already in-flight
    if (!force && _docsLoadedAt && Date.now() - _docsLoadedAt < CACHE_TTL) return; // fresh enough
    set({ loading: true, error: null });
    try {
      // GAB API quirks on this table (b8t29jus6):
      //   1. sort params (sort=created_at&order=desc) cause 502 on ALL requests — never use them
      //   2. limit > actual accessible records causes 5xx — page conservatively
      // Strategy: try limit=50 (no sort), fall back to limit=1 paging on failure.
      // Client-side sort handles ordering after fetch.
      let rows: Record<string, unknown>[] = [];
      let fetchOk = false;

      // Attempt 1: bulk fetch (no sort)
      try {
        const res = await fetchRecords<Record<string, unknown>>(TABLES.flDocuments, { limit: 50 });
        rows = res.records;
        fetchOk = true;
        // Continue paginating if exactly 50 came back
        let offset = 50;
        while (res.records.length === 50) {
          try {
            const next = await fetchRecords<Record<string, unknown>>(TABLES.flDocuments, { limit: 50, offset });
            rows = rows.concat(next.records);
            if (next.records.length < 50) break;
            offset += 50;
          } catch { break; }
        }
      } catch { /* fall through to size-1 paging */ }

      // Attempt 2: one-at-a-time paging (no sort) — handles "limit > count" 5xx
      // Skip errors rather than stopping — the table has a ghost record (bad migrated data)
      // that causes 5xx at its position. Skipping lets us load records that come after it.
      if (!fetchOk) {
        let offset = 0;
        let consecutiveErrors = 0;
        while (offset < 500) {
          try {
            const res = await fetchRecords<Record<string, unknown>>(TABLES.flDocuments, { limit: 1, offset });
            if (res.records.length === 0) break; // past the end
            rows = rows.concat(res.records);
            consecutiveErrors = 0;
            offset += 1;
          } catch {
            consecutiveErrors += 1;
            offset += 1; // skip the bad position and try the next
            if (consecutiveErrors >= 3) break; // stop after 3 consecutive failures (server down)
          }
        }
      }

      // Preserve cached data rather than overwriting with empty on server error
      if (rows.length === 0 && get().documents.length > 0) {
        set({ loading: false });
        return;
      }

      const documents = rows.map((row: Record<string, unknown>) => mapDocument(row));
      // Sort client-side by publishedAt descending (server sort disabled due to GAB bug)
      documents.sort((a, b) => {
        const ta = a.publishDate ? new Date(a.publishDate).getTime() : 0;
        const tb = b.publishDate ? new Date(b.publishDate).getTime() : 0;
        return tb - ta;
      });
      set({ documents, loading: false, _docsLoadedAt: Date.now() });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load documents' });
    }
  },

  createDocument: async (data) => {
    const F = FIELDS.flDocuments;
    const rawDate = data.publishDate ?? new Date().toISOString();
    const publishedAt = /^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? `${rawDate}T00:00:00Z` : rawDate;
    const fields: Array<[string, string]> = [
      [F.title as string, data.title ?? ''],
      [F.description as string, data.description ?? ''],
      [F.categoryId as string, data.category ?? ''],
      [F.tags as string, data.tags ?? ''],
      [F.status as string, data.status ?? 'published'],
      [F.uploadedBy as string, data.uploadedBy ?? ''],
      [F.publishedAt as string, publishedAt],
      [F.version as string, data.version ?? ''],
      [F.priority as string, data.priority ?? ''],
      [F.fileUrl as string, data.fileUrl ?? ''],
    ];
    if (data.expirationDate) {
      const exp = data.expirationDate;
      fields.push([F.expirationDate as string, /^\d{4}-\d{2}-\d{2}$/.test(exp) ? `${exp}T00:00:00Z` : exp]);
    }
    const rid = await createRecord(TABLES.flDocuments, fields);
    await get().loadDocuments(true);
    return get().documents.find(d => d.id === rid) ?? null;
  },

  updateDocument: async (id, data) => {
    const F = FIELDS.flDocuments;
    const fields: Array<[string, string]> = [];
    if (data.title !== undefined) fields.push([F.title as string, data.title]);
    if (data.description !== undefined) fields.push([F.description as string, data.description]);
    if (data.category !== undefined) fields.push([F.categoryId as string, data.category]);
    if (data.tags !== undefined) fields.push([F.tags as string, data.tags]);
    if (data.status !== undefined) fields.push([F.status as string, data.status]);
    if (data.uploadedBy !== undefined) fields.push([F.uploadedBy as string, data.uploadedBy]);
    if (data.version !== undefined) fields.push([F.version as string, data.version]);
    if (data.priority !== undefined) fields.push([F.priority as string, data.priority]);
    if (data.fileUrl !== undefined) fields.push([F.fileUrl as string, data.fileUrl]);
    if (data.publishDate !== undefined) {
      const d = data.publishDate;
      const iso = /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00Z` : d;
      fields.push([F.publishedAt as string, iso]);
    }
    if (data.expirationDate !== undefined) {
      const d = data.expirationDate;
      const iso = d ? (/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00Z` : d) : '';
      if (iso) fields.push([F.expirationDate as string, iso]);
    }
    if (fields.length > 0) {
      await updateRecord(TABLES.flDocuments, id, fields);
      await get().loadDocuments();
    }
  },

  deleteDocument: async (id) => {
    await deleteRecord(TABLES.flDocuments, id);
    set({ documents: get().documents.filter(d => d.id !== id) });
  },

  // ── V2 Native File Upload ────────────────────────────────────
  // Uses the same shared uploadFileToV2 utility as debris-store.ts.
  // Flow: 1) request presigned URL  2) PUT via /api/s3upload proxy  3) store documentId in description

  uploadFile: async (id, file) => {
    const { attachmentId, documentId } = await uploadFileToV2(file, TABLES.flDocuments);

    // Store documentId in description field with meta marker.
    // ATTACHMENT field type doesn't persist via PATCH in V2. documentId (NOT attachmentId)
    // is required for the download-url endpoint.
    const F = FIELDS.flDocuments;
    const existing = get().documents.find(d => d.id === id);
    const currentDesc = existing?.description || '';
const descWithDocId = `${currentDesc}|||DOCID|||${documentId}|||ATTACHID|||${attachmentId}|||`;
    await updateRecord(TABLES.flDocuments, id, [
      [F.file as string, attachmentId],
      [F.description as string, descWithDocId],
    ]);
    await get().loadDocuments(true);
  },

  // ── Get Presigned Download URL ──────────────────────────────
  // Uses the same shared getV2FileDownloadUrl utility as debris-store.ts.

  getFileDownloadUrl: async (documentId: string) => {
    return getV2FileDownloadUrl(documentId);
  },

  // ── Categories ──────────────────────────────────────────────

  loadCategories: async (force = false) => {
    const { _catsLoadedAt, loading } = get();
    if (!force && loading) return;
    if (!force && _catsLoadedAt && Date.now() - _catsLoadedAt < CACHE_TTL) return;
    set({ loading: true, error: null });
    try {
      const data = await fetchTable(TABLES.flCategories, { limit: 200 });
      const categories = data.map((row: Record<string, unknown>) => mapCategory(row));
      set({ categories, loading: false, _catsLoadedAt: Date.now() });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load categories' });
    }
  },

  createCategory: async (data) => {
    const F = FIELDS.flCategories;
    const fields: Array<[string, string]> = [
      [F.name as string, data.name ?? ''],
      [F.color as string, data.color ?? ''],
      [F.sortOrder as string, data.sortOrder ?? ''],
    ];
    if (F.description) fields.push([F.description as string, '']);
    const rid = await createRecord(TABLES.flCategories, fields);
    await get().loadCategories();
    return get().categories.find(c => c.id === rid) ?? null;
  },

  updateCategory: async (id, data) => {
    const F = FIELDS.flCategories;
    const fields: Array<[string, string]> = [];
    if (data.name !== undefined) fields.push([F.name as string, data.name]);
    if (data.color !== undefined) fields.push([F.color as string, data.color]);
    if (data.sortOrder !== undefined) fields.push([F.sortOrder as string, data.sortOrder]);
    if (fields.length > 0) {
      await updateRecord(TABLES.flCategories, id, fields);
      await get().loadCategories();
    }
  },

  deleteCategory: async (id) => {
    await deleteRecord(TABLES.flCategories, id);
    set({ categories: get().categories.filter(c => c.id !== id) });
  },
}));
